import { Check, X } from "lucide-react";
import { useDialogs } from "./DialogsContext";

export default function Pricing() {
  const { openAudit } = useDialogs();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 lg:py-32 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-4">
            / Pricing
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase max-w-4xl mx-auto">
            One model. Zero risk.
          </h2>
          <p className="mt-6 text-white/60 max-w-xl mx-auto">
            We don’t sell retainers. We don’t sell software seats. We share in
            the revenue we actually generate — and nothing else.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Other agencies */}
          <div
            data-testid="pricing-traditional"
            className="bg-[#0a0a0a] border border-white/10 p-8 relative"
          >
            <div className="text-xs uppercase tracking-widest text-white/40 mb-4">
              Traditional agencies
            </div>
            <div className="font-display text-3xl uppercase mb-1 text-white/60">
              $3k–$15k
            </div>
            <div className="text-sm text-white/40 mb-8">/ month upfront</div>
            <ul className="space-y-3 text-sm text-white/60">
              {[
                "Long contracts",
                "Pay even when results don’t show",
                "Tool licenses on top",
                "You manage the agency managing the tools",
              ].map((t, i) => (
                <li key={i} className="flex gap-3">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* PipelineForge */}
          <div
            data-testid="pricing-us"
            className="relative bg-[#0a0a0a] border-2 border-[#ccff00] p-8 lg:scale-[1.03]"
          >
            <div className="absolute -top-3 left-6 px-3 py-1 bg-[#ccff00] text-black text-[10px] uppercase tracking-widest font-bold">
              The PipelineForge way
            </div>
            <div className="text-xs uppercase tracking-widest text-[#ccff00] mb-4 mt-2">
              Performance only
            </div>
            <div className="font-display text-3xl uppercase mb-1">
              <span className="text-[#ccff00]">$0</span> upfront
            </div>
            <div className="text-sm text-white/60 mb-8">
              + share of recovered revenue
            </div>
            <ul className="space-y-3 text-sm text-white/80">
              {[
                "Free audit, free reactivation plan",
                "Pay only on closed deals",
                "All tools, copy, automation included",
                "Cancel anytime — no contracts",
                "Transparent dashboard, exportable receipts",
              ].map((t, i) => (
                <li key={i} className="flex gap-3">
                  <Check className="w-4 h-4 text-[#ccff00] mt-0.5 shrink-0" strokeWidth={3} />
                  {t}
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-cta"
              onClick={openAudit}
              className="mt-8 w-full h-12 bg-[#ccff00] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#b3e600] transition-colors"
            >
              Start with a free audit
            </button>
          </div>

          {/* DIY */}
          <div
            data-testid="pricing-diy"
            className="bg-[#0a0a0a] border border-white/10 p-8"
          >
            <div className="text-xs uppercase tracking-widest text-white/40 mb-4">
              DIY with AI tools
            </div>
            <div className="font-display text-3xl uppercase mb-1 text-white/60">
              $200–$2k
            </div>
            <div className="text-sm text-white/40 mb-8">/ month subscriptions</div>
            <ul className="space-y-3 text-sm text-white/60">
              {[
                "5+ subscriptions to wire together",
                "Hours of onboarding & testing",
                "Bad copy = burned reputation",
                "You’re the strategist, not the tools",
              ].map((t, i) => (
                <li key={i} className="flex gap-3">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
